import { useEffect, useState } from 'react'

// Tuned constants from the design handoff (header-hide-on-scroll.html). These are
// the edge cases that matter — keep them in sync with the reference.
const TOP_GUARD = 8 // always show the bar within this many px of the top (covers overscroll)
const HIDE_AFTER = 80 // don't allow hiding until scrolled at least this far from the top
const DELTA = 5 // ignore direction changes smaller than this so trackpad/touch jitter can't flicker the bar

interface HideOnScroll {
  /** True when the bar should slide out of view (scrolling down past the threshold). */
  hidden: boolean
  /** True once the page has scrolled past the top guard — used for the subtle lift shadow. */
  scrolled: boolean
}

/**
 * Quick-return hidey-bar for a fixed top bar: hides on scroll-down, reveals on scroll-up, always shown
 * at the top. `enabled: false` pins it open and tears the listener down. The transition lives in CSS.
 */
export function useHideOnScroll(enabled = true): HideOnScroll {
  const [hidden, setHidden] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setHidden(false)
      setScrolled(false)
      return
    }

    // Clamp out iOS Safari rubber-band overscroll. Bouncing past the top/bottom drives
    // window.scrollY beyond [0, maxScroll], and the spring-back reads as a reversed
    // direction — falsely revealing the bar at the bottom of the page. Clamping pins
    // scrollY to the real range so the bounce produces no delta. (Other engines don't
    // rubber-band, so this is a no-op there.)
    const clampedScrollY = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight
      return Math.max(0, Math.min(window.scrollY, maxScroll))
    }

    let lastY = clampedScrollY()
    let ticking = false

    const apply = () => {
      const y = clampedScrollY()
      setScrolled(y > TOP_GUARD)

      if (y <= TOP_GUARD) {
        setHidden(false)
        lastY = y
        return
      }

      const delta = y - lastY
      if (Math.abs(delta) < DELTA) return // debounce tiny moves (note: lastY is intentionally not updated)

      if (delta > 0 && y > HIDE_AFTER) {
        setHidden(true) // scrolling down, past threshold
      } else if (delta < 0) {
        setHidden(false) // scrolling up — reveal immediately
      }
      lastY = y
    }

    const onScroll = () => {
      if (!ticking) {
        ticking = true
        window.requestAnimationFrame(() => {
          apply()
          ticking = false
        })
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    apply() // sync initial state (e.g. restored scroll position on navigation)
    return () => window.removeEventListener('scroll', onScroll)
  }, [enabled])

  return { hidden, scrolled }
}
